/**
 * CalendarsPage — CRUD cho bảng market_calendar (Sprint 5).
 * Type MarketCalendar: futures_month (PK), first_notice_day, last_trading_day.
 * Permission: purchase.refs.calendar EDIT — RoleGuard.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { useForm } from 'react-hook-form'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import FormDialog from '@/components/FormDialog'
import PageDataState from '@/components/PageDataState'
import RoleGuard from '@/components/RoleGuard'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useListCalendarsQuery,
  useCreateCalendarMutation,
  useUpdateCalendarMutation,
  useDeleteCalendarMutation,
} from '@/store/api/refsCrudApi'
import { useDebounce } from '@/hooks/useDebounce'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import type { MarketCalendar } from '@/types/api'

interface CalFormValues {
  futures_month: string
  first_notice_day: string
  last_trading_day: string
}

const EMPTY: CalFormValues = { futures_month: '', first_notice_day: '', last_trading_day: '' }

export function CalendarsPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })
  const [editing, setEditing] = useState<MarketCalendar | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<MarketCalendar | null>(null)

  const { data, isLoading, error, isFetching } = useListCalendarsQuery({
    q: debouncedSearch || undefined,
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  })

  const [createCalendar, { isLoading: creating }] = useCreateCalendarMutation()
  const [updateCalendar, { isLoading: updating }] = useUpdateCalendarMutation()
  const [deleteCalendar, { isLoading: deleting }] = useDeleteCalendarMutation()

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing
  const dialogLoading = creating || updating

  const { register, handleSubmit, reset } = useForm<CalFormValues>({ defaultValues: EMPTY })

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      reset({
        futures_month: editing.futures_month,
        first_notice_day: editing.first_notice_day ?? '',
        last_trading_day: editing.last_trading_day ?? '',
      })
    } else {
      reset(EMPTY)
    }
  }, [dialogOpen, isEdit, editing, reset])

  const rows = useMemo(
    () => (data?.items ?? []).map((r) => ({ id: r.futures_month, ...r })),
    [data],
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'futures_month', headerName: 'Futures Month', width: 180 },
      { field: 'first_notice_day', headerName: 'First Notice Day', width: 160 },
      { field: 'last_trading_day', headerName: 'Last Trading Day', width: 160 },
      {
        field: '_actions',
        headerName: 'Thao tác',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Sửa">
              <IconButton size="small" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditing(params.row as MarketCalendar) }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Xoá">
              <IconButton size="small" color="error" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setConfirmDelete(params.row as MarketCalendar) }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleCloseDialog = () => { setCreateOpen(false); setEditing(null) }

  const onSubmit = handleSubmit(async (values) => {
    const payload: MarketCalendar = {
      futures_month: values.futures_month.trim(),
      first_notice_day: values.first_notice_day || null,
      last_trading_day: values.last_trading_day || null,
    }
    try {
      if (isEdit && editing) {
        await updateCalendar({ id: editing.futures_month, body: payload }).unwrap()
        snack.success(`Đã cập nhật calendar "${editing.futures_month}"`)
      } else {
        await createCalendar(payload).unwrap()
        snack.success(`Đã tạo calendar "${payload.futures_month}"`)
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, isEdit ? 'Cập nhật thất bại' : 'Tạo thất bại'))
    }
  })

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteCalendar(confirmDelete.futures_month).unwrap()
      snack.success(`Đã xoá "${confirmDelete.futures_month}"`)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Xoá thất bại'))
    }
  }

  const isEmpty = !isLoading && !error && (data?.items?.length ?? 0) === 0

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>Market Calendars</Typography>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!canEdit} onClick={() => setCreateOpen(true)}>
          Thêm Calendar
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TextField
          label="Tìm kiếm"
          placeholder="Futures month..."
          size="small"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 0 })) }}
          sx={{ minWidth: 260 }}
        />
      </Paper>

      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <Paper>
          <DataTableMUI
            rows={rows}
            columns={columns}
            loading={isFetching}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowCount={data?.total ?? 0}
            onRowClick={(p) => setEditing(p.row as MarketCalendar)}
          />
        </Paper>
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Sửa Calendar' : 'Thêm Calendar'}
        loading={dialogLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void onSubmit()}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="Futures Month *" size="small" fullWidth {...register('futures_month')} disabled={isEdit} placeholder="e.g. H25" />
          <TextField
            label="First Notice Day"
            size="small"
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            {...register('first_notice_day')}
          />
          <TextField
            label="Last Trading Day"
            size="small"
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            {...register('last_trading_day')}
          />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Xoá Calendar"
        message={confirmDelete ? `Xác nhận xoá calendar "${confirmDelete.futures_month}"?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  )
}

export default CalendarsPage
